import { ApiHelper, UsersApiHelper } from "api/ApiHelper";
import { TEMPLATE_URL_PATH } from "components/Financial/FinancialHelpers";
import ExcelJS from "exceljs";
import fileDownload from "js-file-download";

import { reverseFarms578ImportTransformsForExport } from "./farms578Codes";

export const TEMPLATE_FILE_NAME = "Farms 578 - Template.xlsx";
export const SHEET_NAME_EXPECTED = "Farms";
export const SHARE_PERCENT_KEYS = [
  "share_percent",
  "share_percent_2",
  "share_percent_3",
  "share_percent_4",
  "share_percent_5",
];

/** Share columns are Percentage-format cells, and the importer reads the decimal as-is. */
export const SHARE_PERCENT_NUM_FMT = "0.##%";

/**
 * Only for columns whose row 1 key differs from the crop field it targets; everything else passes
 * straight through. The date column used to be one of these, keyed `planting_date` against
 * `planted_date`, which is why the date never reached the API on re-import — the template now
 * names the field, so no alias is needed.
 */
const FIELD_MAP = {
  fsa_farm_name: { field: "farm_name" },
};

export async function exportFarms578Workbook(globalState, crop_year, crops, enqueueSnackbar) {
  const urlResponse = await UsersApiHelper.getPublicDocument(
    { document_path: TEMPLATE_URL_PATH, document_name: TEMPLATE_FILE_NAME },
    globalState
  );

  const templateUrl = urlResponse?.url;
  if (!templateUrl) {
    throw new Error("Could not retrieve template URL.");
  }

  const templateResponse = await fetch(templateUrl);
  if (!templateResponse.ok) {
    throw new Error(`Template download failed: ${templateResponse.status}`);
  }
  const arrayBuffer = await templateResponse.arrayBuffer();

  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);

  const sheet = workbook.worksheets[0];
  if (!sheet) {
    throw new Error("Template workbook has no worksheets.");
  }
  if (sheet.name !== SHEET_NAME_EXPECTED) {
    console.warn(`Expected sheet name "${SHEET_NAME_EXPECTED}", got "${sheet.name}"`);
  }

  const headerRow = sheet.getRow(1);
  const colMap = {};
  headerRow.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    const key = cell.value;
    if (key) colMap[key] = colNumber;
  });

  const lastRow = sheet.rowCount;
  if (lastRow >= 3) {
    sheet.spliceRows(3, lastRow - 2);
  }

  if (!crops?.length) {
    enqueueSnackbar?.("No crops found for the selected year — nothing to export.", { variant: "warning" });
    return;
  }

  const commodityXrefs = (await ApiHelper.selectObject("FSA.ARCPLC", "FSACommodities", {})) ?? [];

  crops.forEach((currentCrop) => {
    const crop = reverseFarms578ImportTransformsForExport(currentCrop, commodityXrefs);

    const rowValues = [];
    Object.entries(colMap).forEach(([key, colNum]) => {
      let value;
      if (key in FIELD_MAP) {
        const { field, format } = FIELD_MAP[key];
        const v = crop[field];
        value = format ? format(v) : (v ?? null);
      } else if (SHARE_PERCENT_KEYS.includes(key)) {
        const v = crop[key];
        value = v !== null && v !== undefined ? Math.round(Number(v) * 10000) / 10000 : null;
      } else {
        const v = crop[key];
        value = v !== undefined ? v : null;
      }
      rowValues[colNum - 1] = value;
    });

    const newRow = sheet.addRow(rowValues);
    Object.entries(colMap).forEach(([key, colNum]) => {
      if (SHARE_PERCENT_KEYS.includes(key) && rowValues[colNum - 1] !== null) {
        newRow.getCell(colNum).numFmt = SHARE_PERCENT_NUM_FMT;
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  fileDownload(buffer, `Farms-578-Export-${crop_year}.xlsx`);
}
